import {match} from 'ts-pattern';

import {useDispatch} from 'react-redux';

import {isNil} from 'ramda';

import {useLazyGetCurrentUserInfoQuery} from '@dms/api/customer';
import {
  newVehicleApi,
  useApplyPricedItemChangeBatchMutation,
  type PricedItemBatchOperation,
  type PricelistVersionRequestBody,
} from '@dms/api/new-vehicle';

import {PricedItemBatchOutcomeUnknown} from '../types/PricedItemBatchOutcomeUnknown';
import {isUnknownBatchOutcome, readBackBatchOutcome} from '../utils/readBackBatchOutcome';

/**
 * Sends one batch of priced item changes. A batch with a version whose response was lost (network
 * error, 5xx without body) is read back (spec 7.5): written, it resolves as a success and reads the
 * year and its lists again; not written, it rejects with the lost response's error; a failed
 * read-back rejects with PricedItemBatchOutcomeUnknown, as the batch may be written.
 * A lost batch without a version reads the year and its lists again and rejects.
 * `readOutcome` reads back a batch sent before, as a group whose outcome was unknown does.
 */
export function useSendPricedItemBatch() {
  const [applyBatch] = useApplyPricedItemChangeBatchMutation();
  const [getModelYear] = newVehicleApi.useLazyGetModelYearQuery();
  const [getHistory] = newVehicleApi.useLazyListPricelistVersionHistoryQuery();
  const [getCurrentUser] = useLazyGetCurrentUserInfoQuery();
  const dispatch = useDispatch();

  /**
   * Reads back a batch sent with `sent`. A written batch reads the year and its lists again; so does
   * an unknown outcome, as the batch may be written and the lists must show what the server holds.
   */
  const readOutcome = async (modelYearId: string, sent: PricelistVersionRequestBody) => {
    const outcome = await readBackBatchOutcome({
      sent,
      readLabel: () =>
        getModelYear({modelYearId}, false)
          .unwrap()
          .then((year) => year.pricelistVersionLabel),
      readNewestVersion: () =>
        getHistory({modelYearId}, false)
          .unwrap()
          .then((history) => history.items.at(0)),
      readCurrentUserId: () =>
        getCurrentUser(undefined, true)
          .unwrap()
          .then((user) => user.id),
    });
    if (outcome === 'written') {
      dispatch(
        newVehicleApi.util.invalidateTags(['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring'])
      );
    }
    if (outcome === 'unknown') {
      dispatch(
        newVehicleApi.util.invalidateTags([
          {type: 'NewVehicleCatalogueResource', id: 'modelYear'},
          'NewVehicleCatalogueAuthoring',
        ])
      );
    }
    return outcome;
  };

  const send = (
    modelYearId: string,
    operations: PricedItemBatchOperation[],
    pricelistVersion?: PricelistVersionRequestBody
  ) =>
    applyBatch({
      modelYearId,
      pricedItemChangeBatchRequestBody: {pricelistVersion, operations},
    })
      .unwrap()
      .then(() => undefined)
      .catch(async (error: unknown) => {
        if (!isUnknownBatchOutcome(error)) {
          throw error;
        }
        if (isNil(pricelistVersion)) {
          // No version marks the batch: read the year and its lists again, so they show what the
          // server holds, whether the batch was written or not (spec 7.5).
          dispatch(
            newVehicleApi.util.invalidateTags([
              {type: 'NewVehicleCatalogueResource', id: 'modelYear'},
              'NewVehicleCatalogueAuthoring',
            ])
          );
          throw error;
        }
        return match(await readOutcome(modelYearId, pricelistVersion))
          .with('written', () => undefined)
          .with('notWritten', () => {
            throw error;
          })
          .with('unknown', () => {
            throw new PricedItemBatchOutcomeUnknown();
          })
          .exhaustive();
      });

  return {send, readOutcome};
}
